// ./src/hooks/useRefreshOnWriteTimeout.ts
//
// ROADMAP #131 — a list re-reads itself when a save timed out, once no drawer is open (the gate in `config/apollo.ts`). The save may have landed after the app stopped
// waiting (the drawer says so); re-reading lets the host SEE whether it did, instead of retrying into a duplicate.
// `@em/api` announces every write that timed out (`onWriteTimeout`); every list owner subscribes here, ONCE, with its own
// re-read. ▶ check-request-timeout sweeps that every list owner does.
import { useEffect, useRef } from "react";
import { onWriteTimeout } from "@em/api";

/**
 * Re-read with `refresh` whenever a write times out — only once the list has been read (`enabled`): a list never shown
 * has nothing stale to correct.
 */
export const useRefreshOnWriteTimeout = (refresh: () => unknown, enabled: boolean): void => {
  const refreshRef = useRef(refresh);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    return onWriteTimeout(() => {
      // A re-read that fails shows its own error; it must not become an unhandled rejection.
      Promise.resolve()
        .then(() => refreshRef.current())
        .catch(() => undefined);
    });
  }, [enabled]);
};
